import { Button } from "@/components/ui/button";
import { Flame } from "lucide-react";
import { openWhatsApp } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/icons/BrandIcons";

const Hero = () => {
  return (
    <section
      aria-label="Alraheem Goat Farm — Pakistan's first live-tracked goat farm for Qurbani 2027 and Qurbani 2026"
      className="relative min-h-[100vh] flex items-center justify-center overflow-hidden bg-gradient-to-b from-background via-primary/5 to-background pt-20 lg:pt-24 pb-10"
    >
      {/* Screen-reader + crawler-only SEO descriptor (keyword-dense English summary) */}
      <span className="sr-only">
        Alraheem Goat Farm is Pakistan's first live-tracked goat farm, located
        in Village Deen Muhammad Bugti, Daur, Sindh. Premium Tedda (Teddy),
        Gulabi, and Kamori bakras for Eid ul Adha Qurbani 2027 (early booking)
        and limited Qurbani 2026 stock — every animal completely self-feed.
        Every goat has live photos, weekly weight tracking, and vet records visible to
        its buyer through a private customer portal. Book on WhatsApp at
        +92 307 2260256.
      </span>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(34,197,94,0.12),transparent_60%)]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-5 lg:mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full border border-primary/20">
            <span className="text-base">🌾</span>
            <span className="text-[11px] sm:text-xs font-semibold text-primary uppercase tracking-wide">
              Pakistan ki pehli tech-enabled bakra farm
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
          {/* Left: Copy */}
          <div className="space-y-5 lg:space-y-7 order-2 lg:order-1 text-center lg:text-left">
            <h1 className="text-[28px] leading-[1.15] sm:text-5xl lg:text-[56px] font-extrabold text-foreground">
              Ab apna bakra <span className="text-primary">live dekhein</span>,
              <br className="hidden sm:block" /> roz, apne phone par.
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Hum Daur, Sindh mein har bakre ki roz ki photo, wazan chart aur vet record share karte hain — taake
              aap ghar baithe apna janwar dekhein. Har bakra tagged, tracked, aur weekly photographed hota hai. Tedda, Gulabi, aur Kamori —{" "}
              <span className="font-semibold text-foreground">Qurbani 2027 ke liye abhi reserve</span>{" "}
              karein — bohot se gharaney pehle hi book kar chuke hain; un bakron ki dekh-bhaal ab se
              hum kar rahe hain. <span className="font-semibold text-foreground">Qurbani 2026</span> ke
              liye sirf chand janwar bache hain. Tamam janwar{" "}
              <span className="font-semibold text-foreground">completely self-feed</span> pale jate
              hain — Eid tak har hafte weight record ke sath apne bakre ko barhta hua dekhein.
            </p>

            {/* Trust row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-center lg:justify-start text-xs sm:text-sm">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                <span className="font-semibold text-foreground">229</span> bakre
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                <span className="font-semibold text-foreground">23</span> happy customers
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                <span className="font-semibold text-foreground">100%</span> vaccinated
              </span>
            </div>

            {/* CTAs */}
            <div className="flex justify-center lg:justify-start">
              <Button
                size="lg"
                className="h-12 min-h-0 w-full gap-2 rounded-full px-5 text-sm font-bold shadow-xl shadow-primary/30 transition-all hover:shadow-2xl sm:h-14 sm:w-auto sm:px-8 sm:text-base md:text-lg [&_svg]:!h-5 [&_svg]:!w-5 sm:[&_svg]:!h-6 sm:[&_svg]:!w-6"
                onClick={() => openWhatsApp("hero")}
              >
                <WhatsAppIcon className="h-5 w-5 shrink-0 text-[#25D366] sm:h-6 sm:w-6" />
                Apna Bakra Book Karein
              </Button>
            </div>

            {/* Urgency */}
            <div className="flex items-center gap-2 justify-center lg:justify-start text-xs sm:text-sm">
              <Flame className="w-4 h-4 text-red-500 animate-pulse" />
              <span className="text-red-600 font-semibold">
                Qurbani 2027 booking chal rahi hai — 2026 ke liye sirf chand janwar bache. Abhi
                WhatsApp pe book karein
              </span>
            </div>
          </div>

          {/* Right: Image with floating stats — no opacity fade: LCP must paint immediately */}
          <div className="relative order-1 lg:order-2">
            <div
              className="relative rounded-3xl overflow-hidden shadow-2xl w-full h-[260px] sm:h-[400px] lg:h-[560px]"
              style={{
                background:
                  "linear-gradient(135deg, hsl(142 20% 96%) 0%, hsl(142 45% 85%) 100%)",
              }}
            >
              <img
                src="/hero-goat-960.webp"
                srcSet="/hero-goat-480.webp 480w, /hero-goat-640.webp 640w, /hero-goat-960.webp 960w"
                sizes="(max-width: 1023px) calc(100vw - 2rem), 480px"
                alt="Premium black-and-white Tedda bakra at Alraheem Goat Farm, Daur, Sindh — live-tracked for Qurbani 2027 and Qurbani 2026"
                title="Alraheem Goat Farm — Live-Tracked Bakras for Qurbani 2027 & 2026"
                className="absolute inset-0 h-full w-full object-contain object-top"
                width={960}
                height={1240}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />

              {/* Corner live badge */}
              <div className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                </span>
                LIVE TRACKING
              </div>

              {/* Floating Stats */}
              <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-2 sm:gap-3 z-20">
                <div className="bg-white/95 backdrop-blur-md rounded-2xl p-3 sm:p-5 text-center border border-white/50 shadow-lg">
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary mb-1 leading-none">
                    229
                  </p>
                  <p className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Animals
                  </p>
                </div>
                <div className="bg-white/95 backdrop-blur-md rounded-2xl p-3 sm:p-5 text-center border border-white/50 shadow-lg">
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary mb-1 leading-none">
                    24/7
                  </p>
                  <p className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Tracking
                  </p>
                </div>
                <div className="bg-white/95 backdrop-blur-md rounded-2xl p-3 sm:p-5 text-center border border-white/50 shadow-lg">
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary mb-1 leading-none">
                    100%
                  </p>
                  <p className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Vaccinated
                  </p>
                </div>
              </div>
            </div>
            <p className="text-center text-xs sm:text-sm text-muted-foreground mt-3 italic">
              Ye screen aap ko bhi milegi jab aap bakra book karain.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
